/**
 * Pagination
 */

if (!customElements.get('pagination-theme')) {
  class Pagination extends HTMLElement {
    constructor() {
      super();

      const sectionId = this.dataset.section;

      this.button = this.querySelector('.load-more');
      this.grid = document.querySelector(
        `[data-id="${CSS.escape(sectionId)}"]`
      );

      this.animationsEnabled =
        document.body.classList.contains('animations-true') &&
        typeof gsap !== 'undefined';

      this.nextUrl = this.dataset.nextUrl || '';
      this.paginationComplete = !this.nextUrl;
      this.observer = null;
      this.preloadDistance = 1800;
    }

    connectedCallback() {
      if (this.classList.contains('pagination-type--loadmore')) {
        this.setupLoadMore();
      }

      if (this.classList.contains('pagination-type--infinite')) {
        this.setupInfiniteScroll();
      }
    }

    disconnectedCallback() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    }

    setupLoadMore() {
      if (!this.button) {
        return;
      }

      if (this.paginationComplete) {
        this.stopPagination();
        return;
      }

      this.button.addEventListener('click', (event) => {
        event.preventDefault();

        this.loadProducts();
        this.button.blur();
      });
    }

    setupInfiniteScroll() {
      if (this.paginationComplete) {
        this.stopPagination();
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          const paginationIsVisible = entries.some(
            (entry) => entry.isIntersecting
          );

          if (
            paginationIsVisible &&
            !this.paginationComplete &&
            !this.hasAttribute('loading')
          ) {
            this.loadProducts();
          }
        },
        {
          root: null,
          rootMargin: `${this.preloadDistance}px 0px ${this.preloadDistance}px 0px`,
          threshold: 0
        }
      );

      this.observer.observe(this);
    }

    isNearViewport() {
      const position = this.getBoundingClientRect();

      return (
        position.top <=
          window.innerHeight + this.preloadDistance &&
        position.bottom >= -this.preloadDistance
      );
    }

    loadProducts() {
      if (
        this.paginationComplete ||
        this.hasAttribute('loading') ||
        !this.nextUrl
      ) {
        return;
      }

      const requestedUrl = new URL(
        this.nextUrl,
        window.location.origin
      ).toString();

      this.setAttribute('loading', 'true');

      fetch(requestedUrl)
        .then((response) => {
          if (!response.ok) {
            throw new Error(
              `Pagination request failed: ${response.status}`
            );
          }

          return response.text();
        })
        .then((responseText) => {
          const parsedDocument = new DOMParser().parseFromString(
            responseText,
            'text/html'
          );

          const incomingGrid =
            parsedDocument.getElementById('product-grid');

          if (!incomingGrid) {
            this.stopPagination();
            return;
          }

          const nextPagination =
            parsedDocument.querySelector(
              `pagination-theme[data-section="${CSS.escape(
                this.dataset.section
              )}"]`
            );

          this.nextUrl =
            nextPagination?.dataset.nextUrl || '';

          const appendedProducts =
            this.renderProducts(incomingGrid);

          dispatchCustomEvent('pagination:page-changed', {
            url: requestedUrl
          });

          if (!this.nextUrl) {
            this.stopPagination();
            return;
          }

          this.finishLoading(appendedProducts);
        })
        .catch((error) => {
          console.error(
            'Could not load additional products.',
            error
          );

          this.removeAttribute('loading');
        });
    }

    renderProducts(incomingGrid) {
      if (!this.grid) {
        return [];
      }

      const incomingColumns = Array.from(
        incomingGrid.querySelectorAll(':scope > .column')
      );

      const appendedColumns = [];

      incomingColumns.forEach((column) => {
        const styleNumber = column.dataset.styleNumber;
        const productId = column.dataset.productId;

        const isBreakout =
          column.dataset.productBreakout === 'true';

        /*
         * Promotions and products without a style number render
         * normally.
         */
        if (!styleNumber) {
          this.appendColumn(column, appendedColumns);
          return;
        }

        const existingStyleProducts = Array.from(
          this.grid.querySelectorAll(
            '[data-style-number]'
          )
        ).filter((existingProduct) => {
          return (
            existingProduct.dataset.styleNumber ===
            styleNumber
          );
        });

        const productAlreadyRendered =
          productId &&
          existingStyleProducts.some(
            (existingProduct) => {
              return (
                existingProduct.dataset.productId ===
                productId
              );
            }
          );

        if (productAlreadyRendered) {
          return;
        }

        if (isBreakout) {
          /*
           * Remove the standard representative when an explicitly
           * broken-out product is discovered on a later page.
           */
          existingStyleProducts.forEach(
            (existingProduct) => {
              const existingIsBreakout =
                existingProduct.dataset
                  .productBreakout === 'true';

              if (!existingIsBreakout) {
                existingProduct.remove();
              }
            }
          );

          this.appendColumn(column, appendedColumns);
          return;
        }

        /*
         * Do not render a regular sibling when the same style was
         * already rendered on a prior page.
         */
        if (existingStyleProducts.length > 0) {
          return;
        }

        this.appendColumn(column, appendedColumns);
      });

      appendedColumns.forEach((column) => {
        column
          .querySelectorAll('.lazyload')
          .forEach((image) => {
            if (
              typeof lazySizes !== 'undefined' &&
              lazySizes.loader
            ) {
              lazySizes.loader.unveil(image);
            }
          });
      });

      return appendedColumns;
    }

    appendColumn(column, appendedColumns) {
      this.grid.appendChild(column);
      appendedColumns.push(column);
    }

    finishLoading(products) {
      const loadingFinished = () => {
        this.removeAttribute('loading');

        if (
          this.classList.contains(
            'pagination-type--infinite'
          ) &&
          !this.paginationComplete &&
          this.nextUrl &&
          this.isNearViewport()
        ) {
          /*
           * Continue loading if a page contained only duplicate
           * sibling products and did not increase the grid height.
           */
          requestAnimationFrame(() => {
            this.loadProducts();
          });
        }
      };

      if (
        this.animationsEnabled &&
        products.length > 0
      ) {
        gsap.set(products, {
          opacity: 0,
          y: 30
        });

        gsap.to(products, {
          duration: 0.5,
          y: 0,
          opacity: 1,
          stagger: 0.05,
          onComplete: loadingFinished
        });
      } else {
        loadingFinished();

        if (
          typeof StampedFn !== 'undefined' &&
          typeof StampedFn.loadBadges === 'function'
        ) {
          StampedFn.loadBadges();
        }
      }
    }

    stopPagination() {
      this.paginationComplete = true;
      this.nextUrl = '';

      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }

      this.removeAttribute('loading');

      if (this.button) {
        this.button.style.display = 'none';
        this.button.disabled = true;
      }
    }
  }

  customElements.define(
    'pagination-theme',
    Pagination
  );
}